import { ResumoPedidoPDV } from '@app/modules/pedido-pdv/model/pedido-nfce.model';
import { IPedidoProduto } from '@app/modules/pedidos/model/itens.model';
import { IPedidos } from '@app/modules/pedidos/model/pedidos.model';
import { LiteralObject } from 'viggo-sistemas-core-web';
import { Utils } from '../helpers/utils';

export interface IRegraPedido {
  regra: string;
  valor_regra: any;
  msg_erro: string;
  invalido: boolean;
  item_id?: string;
}

/**
 * Class responsável por validar o pedido e seus itens
 */
export class ValidadorPedido {
  regras = [] as IRegraPedido[];
  regras_validadas = [] as IRegraPedido[];

  regras_tipo_atacado = [
    'controle_estoque',
    'valor_minimo',
    'acrescimo_max',
    'desconto_max',
    'perde_ganha'
  ];

  regras_tipo_varejo = [
    'controle_estoque',
    'valor_minimo',
    'acrescimo_max',
    'desconto_max'
  ];

  perfil: string;

  constructor(
    private pedido: IPedidos | ResumoPedidoPDV,
    private validacoes: LiteralObject,
    private tipo_perfil: string
  ) {
    this.perfil = tipo_perfil;
    this.montarRegras();
  }

  montarRegras() {
    const regras =
      this.perfil === 'ATACADO'
        ? this.regras_tipo_atacado
        : this.regras_tipo_varejo;
    Object.keys(this.validacoes).forEach(parentKey => {
      Object.keys(this.validacoes[parentKey]).forEach(childKey => {
        if (
          regras.includes(childKey) &&
          this.validacoes[parentKey][childKey] !== 'UNKNOWN'
        ) {
          this.regras_validadas.push({
            msg_erro: '',
            regra: childKey,
            invalido: false,
            valor_regra: this.validacoes[parentKey][childKey]
          });
        }
      });
    });
  }

  validarPedidoCompleto() {
    this.pedido.itens.forEach(it => {
      this.validarItem(it);
    });
  }

  tratarValorDesconto(desconto) {
    if (desconto === null || desconto === undefined) return null;

    if (typeof desconto === 'string') {
      if (desconto.includes(',')) {
        return Utils.replaceCommaToDot(desconto);
      } else if (desconto.includes('.')) {
        return Number(desconto);
      } else if (Number.isInteger(Number(desconto))) {
        return Number(desconto);
      } else {
        console.error('Valor inválido para conversão!');
        return null;
      }
    } else {
      if (desconto.toString().includes(',')) {
        return Utils.replaceCommaToDot(desconto);
      } else if (desconto.toString().includes('.')) {
        return Number(desconto);
      } else if (Number.isInteger(desconto)) {
        return Number(desconto);
      } else {
        console.error('Valor inválido para conversão!');
        return null;
      }
    }
  }

  validarItem(item: IPedidoProduto) {
    const descAcres = this.tratarValorDesconto(item.desconto);
    this.regras_validadas.forEach(validar => {
      if (validar.regra === 'controle_estoque' && validar.valor_regra) {
        validar.invalido = this.validarEstoque(item);
        validar.msg_erro = 'Quantidade acima do estoque';
        validar.item_id = item.produto_variacao_id
          ? item.produto_variacao_id
          : item.produto_id;
      }

      if (validar.regra === 'valor_minimo' && !item.produto.em_promocao) {
        const result = this.validarValorMinimo(item, validar.valor_regra);
        if (result) {
          validar.item_id = item.produto_variacao_id
            ? item.produto_variacao_id
            : item.produto_id;
          validar.invalido = result;
          validar.msg_erro =
            validar.valor_regra === 'PRODUTO'
              ? `Não é possível adicionar o item ${
                  item.produto.descricao
                },\n pois o valor mínimo permitido no produto é R$ ${Utils.replaceCommaToDot(
                  item.valor_minimo
                )}`
              : `Não é possível adicionar o item ${
                  item.produto.descricao
                },\n pois o valor mínimo permitido pela tabela de preço é R$ ${Utils.replaceCommaToDot(
                  item.valor_minimo
                )}`;
        }
      }

      if (validar.regra === 'acrescimo_max' && descAcres < 0) {
        validar.item_id = item.produto_variacao_id
          ? item.produto_variacao_id
          : item.produto_id;
        validar.invalido = this.validarDesconto(
          descAcres * -1,
          validar.valor_regra
        );
        validar.msg_erro = 'Valor do acréscimo maior que o máximo.';
      }

      if (validar.regra === 'desconto_max' && descAcres > 0) {
        validar.item_id = item.produto_variacao_id
          ? item.produto_variacao_id
          : item.produto_id;
        validar.invalido = this.validarDesconto(descAcres, validar.valor_regra);
        validar.msg_erro = 'Valor do desconto maior que o máximo.';
      }

      if (validar.regra === 'perde_ganha') {
        var saldoPerdeGanha = this.calcularPerdeGanha();
        var vendedorPerdeGanha = Number(
          localStorage.getItem('vendedor_perde_ganha')
        );
        const vendedor_saldo =
          saldoPerdeGanha + (vendedorPerdeGanha < 0 ? 0 : vendedorPerdeGanha);
        const result =
          validar.valor_regra === 'VENDEDOR'
            ? this.validarPerdeGanhaVendedor()
            : this.validarPerdeGanhaPedido();
        if (result) {
          validar.item_id = item.produto_variacao_id
            ? item.produto_variacao_id
            : item.produto_id;
          validar.invalido = result;
          validar.msg_erro =
            validar.valor_regra === 'VENDEDOR'
              ? `Não é possível finalizar o pedido,\n pois o saldo perde ganha vendedor é menor que 0: R$ ${vendedor_saldo}`
              : `Não é possível adicionar o item,\n pois o saldo perde ganha do pedido é menor que 0: R$ ${saldoPerdeGanha}`;
        }
      }
    });

    this.regras = this.regras_validadas.filter(regra => regra.invalido);
    item.regras = this.regras_validadas.filter(regra => regra.invalido);
  }

  /**
   * Verificar em cada item se algum deles está quebrando
   * a regra de valor mínimo do produto
   * @param item
   */
  validarValorMinimo(item: IPedidoProduto, valor_regra: string) {
    const vlItem =
      item.valor_total_final >= 0
        ? Utils.converteABNT(item.valor_total_final / item.quantidade, 2)
        : Utils.converteABNT(item.valor_total / item.quantidade, 2);
    if (
      valor_regra === 'TABELA_PRECO' &&
      item.valor_minimo === item.produto.valor_minimo
    )
      return false;
    else return vlItem < item.valor_minimo;
  }

  /**
   * Valida se a quantidade itens é maior que a do estoque
   * @param item
   * @returns
   */
  validarEstoque(item: IPedidoProduto) {
    return item.quantidade > item.produto.quantidade;
  }

  /**
   * Valida a regra de desconto e/ou acréscimo máximo aplicado
   * @param item
   * @returns
   */
  validarDesconto(descAcres: number, valor_maximo: number) {
    return descAcres > valor_maximo;
  }

  /**
   * Verificar em cada item se algum deles está quebrando
   * a regra de valor do perde ganha do vendedor
   * @param produto
   */
  validarPerdeGanhaVendedor() {
    var saldo_perde_ganha = Number(
      localStorage.getItem('vendedor_perde_ganha')
    );
    saldo_perde_ganha < 0 ? (saldo_perde_ganha = 0) : saldo_perde_ganha;
    return this.calcularPerdeGanha() + saldo_perde_ganha < 0;
  }

  /**
   * Verificar em cada item se algum deles está quebrando
   * a regra de valor do perde ganha
   * @param produto
   */
  validarPerdeGanhaPedido() {
    return this.calcularPerdeGanha() < 0;
  }

  /**
   * Responsável pelo calculo dos itens de um pedido
   * @param itens
   * @returns
   */
  calcularPerdeGanha() {
    let valorPerdeGanha = 0;
    this.pedido.itens.forEach(i => {
      i.valor_total_inicial = Utils.converteABNT(
        i.valor_unitario * i.quantidade,
        2
      );
      i.valor_total_final = Utils.converteABNT(
        this.calcValorTotalItem(
          i.quantidade,
          i.valor_unitario,
          Number(i.desconto)
        ),
        2
      );
      valorPerdeGanha += i.valor_total_final - i.valor_total_inicial;
      delete i.valor_total_inicial;
      delete i.valor_total_final;
    });
    return valorPerdeGanha;
  }

  /**
   * Responsável por calcular o valor total de um item
   * @param qtd
   * @param valor_unitario
   * @param desconto
   * @returns
   */
  calcValorTotalItem(qtd, valor_unitario, desconto) {
    return (
      Utils.replaceCommaToDot(qtd) *
      valor_unitario *
      (1 - Utils.replaceCommaToDot(desconto) / 100)
    );
  }
}
